import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import FiberNewIcon from '@material-ui/icons/FiberNew';
import LayersIcon from '@material-ui/icons/Layers';
import NewReleasesIcon from '@material-ui/icons/NewReleases';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import BuildIcon from '@material-ui/icons/Build';

const CAT_YELLOW = '#FFCD11';
const CAT_BLACK = '#1A1A1A';
const CAT_DARK_GRAY = '#3D3D3D';
const CAT_MID_GRAY = '#6B6B6B';
const CAT_LIGHT_GRAY = '#F4F4F4';
const CAT_BORDER = '#E0E0E0';

const useStyles = makeStyles(theme => ({
  pageRoot: {
    backgroundColor: '#FFFFFF',
    minHeight: '100vh',
  },
  // ── Hero ─────────────────────────────────────────────────────────────────
  hero: {
    background: `linear-gradient(135deg, ${CAT_BLACK} 0%, #2c2c2c 60%, #3a3014 100%)`,
    padding: theme.spacing(9, 6, 8, 6),
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -80,
      right: -80,
      width: 340,
      height: 340,
      borderRadius: '50%',
      background: 'rgba(255,205,17,0.06)',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: -100,
      left: '38%',
      width: 400,
      height: 400,
      borderRadius: '50%',
      background: 'rgba(255,205,17,0.04)',
    },
  },
  heroInner: {
    maxWidth: 900,
    margin: '0 auto',
  },
  heroBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor: 'rgba(255,205,17,0.15)',
    border: `1px solid rgba(255,205,17,0.4)`,
    borderRadius: 20,
    padding: theme.spacing(0.5, 2),
    marginBottom: theme.spacing(2.5),
  },
  heroBadgeText: {
    color: CAT_YELLOW,
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
  },
  heroTitle: {
    color: '#FFFFFF',
    fontSize: '3rem',
    fontWeight: 800,
    letterSpacing: '-0.6px',
    lineHeight: 1.12,
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: { fontSize: '2rem' },
  },
  heroYellow: { color: CAT_YELLOW },
  heroSubtitle: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: '1.05rem',
    lineHeight: 1.7,
    maxWidth: 560,
    marginBottom: theme.spacing(4),
  },
  heroMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(4),
    flexWrap: 'wrap',
  },
  heroMetaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    color: 'rgba(255,255,255,0.5)',
    fontSize: '0.85rem',
  },
  heroMetaIcon: { fontSize: '1rem', color: CAT_YELLOW },

  // ── Release banner ────────────────────────────────────────────────────────
  releaseBanner: {
    background: `linear-gradient(90deg, rgba(255,205,17,0.1) 0%, rgba(255,205,17,0.02) 100%)`,
    border: `1px solid rgba(255,205,17,0.28)`,
    borderRadius: 12,
    padding: theme.spacing(2, 3),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    maxWidth: 900,
    margin: `${theme.spacing(6)}px auto ${theme.spacing(2)}px`,
  },
  releaseBannerText: {
    color: CAT_DARK_GRAY,
    fontSize: '0.92rem',
  },

  // ── Section label ─────────────────────────────────────────────────────────
  sectionLabel: {
    fontSize: '0.7rem',
    fontWeight: 700,
    color: CAT_MID_GRAY,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    borderLeft: `3px solid ${CAT_YELLOW}`,
    paddingLeft: theme.spacing(1.25),
    maxWidth: 900,
    margin: `0 auto ${theme.spacing(5)}px`,
  },

  // ── Alternating feature rows ──────────────────────────────────────────────
  featureRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(8),
    maxWidth: 1100,
    margin: '0 auto',
    padding: theme.spacing(8, 4),
    borderBottom: `1px solid ${CAT_BORDER}`,
    '&:last-of-type': { borderBottom: 'none' },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      padding: theme.spacing(5, 2),
      gap: theme.spacing(4),
    },
  },
  featureRowReverse: {
    flexDirection: 'row-reverse',
    [theme.breakpoints.down('sm')]: { flexDirection: 'column' },
  },
  featureText: {
    flex: '0 0 42%',
    maxWidth: 420,
    [theme.breakpoints.down('sm')]: { maxWidth: '100%', flex: 'none' },
  },
  featureIconBadge: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: 'rgba(255,205,17,0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    '& svg': { color: '#c9950c', fontSize: '1.5rem' },
  },
  featureTag: {
    display: 'inline-block',
    fontSize: '0.68rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    borderRadius: 4,
    padding: '2px 8px',
    marginBottom: theme.spacing(1.5),
  },
  featureTitle: {
    fontSize: '1.6rem',
    fontWeight: 800,
    color: CAT_BLACK,
    lineHeight: 1.2,
    marginBottom: theme.spacing(1.5),
    letterSpacing: '-0.3px',
  },
  featureDesc: {
    fontSize: '0.95rem',
    color: CAT_MID_GRAY,
    lineHeight: 1.75,
    marginBottom: theme.spacing(2.5),
  },
  bulletList: { padding: 0, margin: 0 },
  bulletItem: { padding: theme.spacing(0.5, 0), alignItems: 'flex-start' },
  bulletIcon: {
    minWidth: 24,
    marginTop: 3,
    '& svg': { fontSize: '0.85rem', color: '#c9950c' },
  },
  bulletText: {
    '& span': { fontSize: '0.88rem', color: CAT_DARK_GRAY, lineHeight: 1.5 },
  },

  // ── Screenshot mockup ─────────────────────────────────────────────────────
  screenshotWrap: {
    flex: 1,
    minWidth: 0,
  },
  screenshotOuter: {
    borderRadius: 14,
    overflow: 'hidden',
    boxShadow: '0 8px 40px rgba(0,0,0,0.13)',
    border: `1px solid ${CAT_BORDER}`,
    background: '#fff',
  },
  screenshotBar: {
    background: '#F0F0F0',
    padding: theme.spacing(1, 1.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.6),
    borderBottom: `1px solid ${CAT_BORDER}`,
  },
  screenshotDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
  },
  screenshotBody: {
    padding: theme.spacing(2),
    minHeight: 240,
    background: CAT_LIGHT_GRAY,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  screenshotImg: {
    width: '100%',
    display: 'block',
    borderRadius: 6,
  },

  // ── Divider ───────────────────────────────────────────────────────────────
  dividerWrap: {
    maxWidth: 1100,
    margin: '0 auto',
    padding: theme.spacing(0, 4),
  },
  dividerYellow: {
    height: 3,
    backgroundColor: CAT_YELLOW,
    width: 60,
    borderRadius: 3,
    margin: `${theme.spacing(6)}px auto ${theme.spacing(5)}px`,
  },

  // ── Older releases ────────────────────────────────────────────────────────
  olderWrap: {
    maxWidth: 860,
    margin: '0 auto',
    padding: theme.spacing(0, 2, 8, 2),
  },
  olderTitle: {
    fontSize: '1.15rem',
    fontWeight: 700,
    color: CAT_DARK_GRAY,
    marginBottom: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  accordion: {
    border: `1px solid ${CAT_BORDER}`,
    borderRadius: '10px !important',
    marginBottom: `${theme.spacing(1.5)}px !important`,
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    '&::before': { display: 'none' },
  },
  accordionSummary: {
    borderRadius: 10,
    '&.Mui-expanded': {
      borderBottom: `1px solid ${CAT_BORDER}`,
      borderRadius: '10px 10px 0 0',
    },
  },
  accordionVersion: {
    fontWeight: 700,
    color: CAT_BLACK,
    fontSize: '1rem',
    flex: 1,
  },
  accordionDate: {
    fontSize: '0.82rem',
    color: CAT_MID_GRAY,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginRight: theme.spacing(2),
  },
  accordionDetails: { padding: theme.spacing(3), display: 'block' },
  patchItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 0),
    '&:not(:last-child)': { borderBottom: `1px solid ${CAT_BORDER}` },
  },
  patchBadge: {
    height: 20,
    fontSize: '0.65rem',
    fontWeight: 700,
    borderRadius: 4,
    flexShrink: 0,
    marginTop: 2,
  },
  patchText: { fontSize: '0.88rem', color: CAT_DARK_GRAY, lineHeight: 1.5 },
}));

// ── Data ─────────────────────────────────────────────────────────────────────

interface ReleaseFeature {
  icon: React.ReactNode;
  title: string;
  tag: string;
  tagColors: { bg: string; text: string };
  description: string;
  bullets: string[];
  /** One or more screenshots. Use an empty src to show a styled placeholder. */
  screenshots: { src: string; label: string }[];
}

interface OldRelease {
  version: string;
  date: string;
  summary: string;
  items: { type: 'new' | 'improved' | 'fixed'; text: string }[];
}

const latestFeatures: ReleaseFeature[] = [
  {
    icon: <VerifiedUserIcon />,
    title: 'Token Validator',
    tag: 'New',
    tagColors: { bg: 'rgba(34,197,94,0.1)', text: '#166534' },
    description:
      'A brand-new utility to decode a JWT access token and validate it against Service Catalog requirements — entirely in your browser, with nothing sent to any server.',
    bullets: [
      'Automatic client-type detection (user based vs. service based)',
      'Issuer and token-type identification (Entra ID, Azure AD B2C)',
      'Mandatory claim checks and expected Client ID (azp) matching',
      'Effective client ID resolution for entitlements, with cataccounttype checks',
      'Full token analysis panel plus the decoded payload for reference',
    ],
    screenshots: [
      { src: '/screenshots/release-notes/token-validator.png', label: 'Input & Validation Results' },
      { src: '/screenshots/release-notes/token-analysis.png', label: 'Token Analysis' },
    ],
  },
  {
    icon: <BuildIcon />,
    title: 'Utilities Menu & Quick Access',
    tag: 'New',
    tagColors: { bg: 'rgba(34,197,94,0.1)', text: '#166534' },
    description:
      'A new collapsible Utilities menu in the sidebar groups the developer tools together, giving direct access to pages that were previously reachable only inside the onboarding flow.',
    bullets: [
      'One-click access to Token Generator, Token Validator, Request Access Form, and Track Adoption Request',
      'Token Generator and Request Access Form are now available as standalone pages',
      'The Track Adoption Request page can now be found under the Utilities menu',
      'Utilities are available as a collapsible dropdown',
    ],
    screenshots: [
      { src: '/screenshots/release-notes/utilities-menu.png', label: 'Utilities Menu' },
    ],
  },
   {
    icon: <BuildIcon />,
    title: 'Functional Leader - Service Details',
    tag: 'New',
    tagColors: { bg: 'rgba(34,197,94,0.1)', text: '#166534' },
    description:
      'Service Details page now displays Functional Leader information',
    bullets: [
      'Show Functional Leader for each Service'
    ],
    screenshots: [
      { src: '/screenshots/release-notes/functional-leader.png', label: 'Functional Leader' },
    ],
  },
];

const olderReleases: OldRelease[] = [
  {
    version: 'Service Catalog Release 2026.06.29',
    date: 'June 29, 2026',
    summary: 'Major feature release — catalog, compliance, and guided onboarding.',
    items: [
      { type: 'new', text: 'API Catalog with smart filters and rich ownership/lifecycle metadata' },
      { type: 'new', text: 'Service Capabilities Matrix for platform-wide capability visibility' },
      { type: 'new', text: 'EIR Compliance Matrix — 11-rule compliance scorecard per service' },
      { type: 'new', text: 'Catalog Graph view with custom CAT-branded dependency nodes' },
      { type: 'new', text: 'Integrated OpenAPI/Swagger specification viewer' },
      { type: 'new', text: 'Helios API Onboarding Page — 6-step guided self-service journey' },
    ],
  },
  {
    version: 'Service Catalog Release 2025.10.30',
    date: 'October 30, 2025 ',
    summary: 'Initial beta — core catalog infrastructure and authentication.',
    items: [
      { type: 'new', text: 'Backstage core bootstrapped with default theme and CAT branding' },
      { type: 'new', text: 'Microsoft Azure AD SSO integration for single sign-on' },
      { type: 'new', text: 'Base API catalog entity ingestion from YAML descriptors' },
      { type: 'new', text: 'Redux store wired up for shared state management' },
      { type: 'new', text: 'Entity Graph framework integrated' },
      { type: 'improved', text: 'Enhanced performance for catalog table column rendering' },
      { type: 'fixed', text: 'Entity list context guard to prevent undefined filter crashes' },
    ],
  },
];

const patchBadgeStyle = (type: 'new' | 'improved' | 'fixed') => {
  if (type === 'new') return { backgroundColor: 'rgba(34,197,94,0.15)', color: '#166534' };
  if (type === 'improved') return { backgroundColor: 'rgba(168,85,247,0.12)', color: '#6b21a8' };
  return { backgroundColor: 'rgba(239,68,68,0.12)', color: '#991b1b' };
};

// ── Mock screenshot placeholder ───────────────────────────────────────────────
function ScreenshotMock({ label, screenshot, classes }: { label: string; screenshot: string; classes: ReturnType<typeof useStyles> }) {
  return (
    <div className={classes.screenshotOuter}>
      <div className={classes.screenshotBar}>
        <div className={classes.screenshotDot} style={{ background: '#FF5F56' }} />
        <div className={classes.screenshotDot} style={{ background: '#FFBD2E' }} />
        <div className={classes.screenshotDot} style={{ background: '#27C93F' }} />
      </div>
      <div className={classes.screenshotBody}>
        {screenshot ? (
          <img src={screenshot} alt={label} className={classes.screenshotImg} />
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            width: '100%',
            minHeight: 220,
          }}>
            {/* Fake table rows to suggest UI */}
            <div style={{ width: '90%', background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
              <div style={{ background: CAT_BLACK, padding: '8px 14px', display: 'flex', gap: 12 }}>
                {['Name', 'Team', 'Owner', 'Status'].map(h => (
                  <div key={h} style={{ flex: 1, height: 8, background: 'rgba(255,205,17,0.5)', borderRadius: 3 }} />
                ))}
              </div>
              {[0.9, 0.7, 0.85, 0.6, 0.75].map((op, i) => (
                <div key={i} style={{ padding: '7px 14px', display: 'flex', gap: 12, borderBottom: `1px solid ${CAT_BORDER}`, opacity: op }}>
                  {[1, 0.8, 0.6, 0.4].map((w, j) => (
                    <div key={j} style={{ flex: 1, height: 7, background: i % 2 === 0 ? '#ddd' : '#e8e8e8', borderRadius: 3, opacity: w }} />
                  ))}
                </div>
              ))}
            </div>
            <span style={{ fontSize: '0.75rem', color: CAT_MID_GRAY, marginTop: 4 }}>{label}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function ReleaseNotes() {
  const classes = useStyles();
  const [expanded, setExpanded] = useState<string | false>(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const handleChange = (panel: string) => (_: React.ChangeEvent<{}>, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <div className={classes.pageRoot}>

      {/* ── Hero ── */}
      <div className={classes.hero}>
        <div className={classes.heroInner}>
          <div className={classes.heroBadge}>
            <NewReleasesIcon style={{ fontSize: '0.9rem', color: CAT_YELLOW }} />
            <span className={classes.heroBadgeText}>What&apos;s New</span>
          </div>
          <Typography className={classes.heroTitle}>
            Service Catalog<br />
            <span className={classes.heroYellow}>Release Notes</span>
          </Typography>
          <Typography className={classes.heroSubtitle}>
            Stay up to date with the latest features, improvements, and fixes
            across the Helios Service Catalog platform.
          </Typography>
          <div className={classes.heroMeta}>
            <span className={classes.heroMetaItem}>
              <CalendarTodayIcon className={classes.heroMetaIcon} />
              Latest: October 14, 2026
            </span>
            <span className={classes.heroMetaItem}>
              <LayersIcon className={classes.heroMetaIcon} />
              Team Cosmos - Service Catalog
            </span>
          </div>
        </div>
      </div>

      {/* ── Release banner ── */}
      <div className={classes.releaseBanner}>
        <FiberNewIcon style={{ color: CAT_YELLOW, fontSize: '1.5rem', flexShrink: 0 }} />
        <Typography className={classes.releaseBannerText}>
          <strong>Service Catalog Release </strong> &nbsp;·&nbsp; October 2026 &nbsp;·&nbsp;
          New developer utilities — a dedicated Token Validator and a Utilities menu for faster access.
        </Typography>
      </div>

      <Typography className={classes.sectionLabel}>
        Latest Release — 2026.10.14
      </Typography>

      {/* ── Alternating feature rows ── */}
      {latestFeatures.map((feature, idx) => {
        const isReverse = idx % 2 !== 0;
        return (
          <div
            key={feature.title}
            className={`${classes.featureRow} ${isReverse ? classes.featureRowReverse : ''}`}
          >
            {/* Text side */}
            <div className={classes.featureText}>
              <div className={classes.featureIconBadge}>{feature.icon}</div>
              <span
                className={classes.featureTag}
                style={{ background: feature.tagColors.bg, color: feature.tagColors.text }}
              >
                {feature.tag}
              </span>
              <Typography className={classes.featureTitle}>{feature.title}</Typography>
              <Typography className={classes.featureDesc}>{feature.description}</Typography>
              <List className={classes.bulletList} disablePadding>
                {feature.bullets.map(b => (
                  <ListItem key={b} className={classes.bulletItem} disableGutters>
                    <ListItemIcon className={classes.bulletIcon}>
                      <CheckCircleOutlineIcon />
                    </ListItemIcon>
                    <ListItemText
                      className={classes.bulletText}
                      primary={b}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </div>

            {/* Screenshot side */}
            <div className={classes.screenshotWrap}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                {feature.screenshots.map(shot => (
                  <ScreenshotMock
                    key={shot.label}
                    label={shot.label}
                    screenshot={shot.src}
                    classes={classes}
                  />
                ))}
              </div>
            </div>
          </div>
        );
      })}

      {/* ── Divider before older releases ── */}
      <div className={classes.dividerYellow} />

      {/* ── Older releases ── */}
      <div className={classes.olderWrap}>
        <Typography className={classes.olderTitle}>
          <CalendarTodayIcon style={{ color: CAT_MID_GRAY, fontSize: '1.1rem' }} />
          Previous Releases
        </Typography>

        {olderReleases.map((release, idx) => (
          <Accordion
            key={release.version}
            className={classes.accordion}
            expanded={expanded === `panel-${idx}`}
            onChange={handleChange(`panel-${idx}`)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary} >
              <Typography className={classes.accordionVersion}>{release.version}</Typography>
              <Typography className={classes.accordionDate}>
                <CalendarTodayIcon style={{ fontSize: '0.8rem' }} />
                {release.date}
              </Typography>
              <Chip
                label={`${release.items.length} changes`}
                size="small"
                style={{ height: 20, fontSize: '0.68rem', backgroundColor: CAT_LIGHT_GRAY, color: CAT_MID_GRAY }}
              />
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              <Typography style={{ fontSize: '0.88rem', color: CAT_MID_GRAY, marginBottom: 16 }}>
                {release.summary}
              </Typography>
              {release.items.map((item, i) => (
                <div key={i} className={classes.patchItem}>
                  <Chip
                    label={item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                    size="small"
                    className={classes.patchBadge}
                    style={patchBadgeStyle(item.type)}
                  />
                  <Typography className={classes.patchText}>{item.text}</Typography>
                </div>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </div>

    </div>
  );
}


